import type { BimContractConversionIndexInput } from 'gql/graphql';
import { useConstructionTypesListItems } from 'pages/Project/Common/hooks/useConstructionTypesListItems';
import { getEditableNumberCell } from 'utils/tableCells';

import { useMemo } from 'react';
import type { Control } from 'react-hook-form';

export interface IUseBimConversionIndicesColumns {
    isEditable?: boolean;
    control?: Control;
}

export enum ConversionCoefficientsKeys {
    kemm = 'kemm',
    kzpm = 'kzpm',
    keqp = 'keqp',
    kmtr = 'kmtr',
    knr = 'knr',
    kozp = 'kozp',
    kpir = 'kpir',
    ksp = 'ksp',
    kmz = 'kmz',
    koz = 'koz',
}

export const conversionCoefficientsKeys = Object.values(ConversionCoefficientsKeys);

export type TBimContractConversionIndexInput = BimContractConversionIndexInput & { id: string };

export const getConversionIndicesCoefficientKey = (key: string, constructionTypeId: number) =>
    `${key}_${constructionTypeId}`;

const coefficientsKey = 'coefficients';

const commonWrapperPadding = 32;
const innerWrapperPadding = 16;

const getInputStyle = (isEditable: boolean | undefined, baseWidth = 85) => {
    const inputWidth = baseWidth - commonWrapperPadding - innerWrapperPadding;

    if (isEditable) {
        return {
            inputStyle: {
                width: `${inputWidth}px`,
                minWidth: `${inputWidth}px`,
            },
        };
    }

    return undefined;
};

export const useBimConversionIndicesColumns = (props?: IUseBimConversionIndicesColumns) => {
    const { isEditable, control } = props || {};
    const { getConstructionTypeName } = useConstructionTypesListItems();

    return useMemo(
        () => [
            {
                title: 'Вид строительства',
                accessor: 'constructionType',
                renderCell: (row: any) => getConstructionTypeName(row?.constructionTypeId, row?.constructionType?.name),
                width: isEditable ? 170 : 231,
            },
            getEditableNumberCell({
                title: 'Козп',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kozp],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kozp, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Кэмм',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kemm],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kemm, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 70,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Кзпм',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kzpm],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kzpm, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 70,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Кмп',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kmtr],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmtr, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 70,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Коп',

                accessor: [coefficientsKey, ConversionCoefficientsKeys.keqp],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.keqp, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 70,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Кмз',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kmz],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kmz, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),

            getEditableNumberCell({
                title: 'Коз',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.koz],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.koz, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Кнр',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.knr],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.knr, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Ксп',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.ksp],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.ksp, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
            getEditableNumberCell({
                title: 'Ки',
                accessor: [coefficientsKey, ConversionCoefficientsKeys.kpir],
                nameGetter: (row: TBimContractConversionIndexInput) =>
                    getConversionIndicesCoefficientKey(ConversionCoefficientsKeys.kpir, row.constructionTypeId),
                isEditable,
                control,
                doNotCheckRowFlag: true,
                width: isEditable ? 85 : 60,
                textFieldWidth: 'full',
                isTooltipHidden: true,
                placeholder: '0',
                ...getInputStyle(isEditable),
            }),
        ],
        [control, getConstructionTypeName, isEditable],
    );
};
